import { useCallback, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Trash2, Clock, MessageSquare } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useTaskAutocomplete } from '../hooks/use-task-autocomplete'
const CommentTimestamp = ({ timestamp }) => (
  <span className="text-xs text-muted-foreground/60 flex items-center gap-1 font-medium">
    <Clock className="h-2.5 w-2.5" />
    {formatDistanceToNow(new Date(timestamp), {
      addSuffix: true,
    })}
  </span>
)
const CommentDeleteButton = ({ commentId, handleDeleteClick, stopPropagation }) => (
  <Button
    variant="ghost"
    size="icon"
    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-all text-muted-foreground/40 hover:text-destructive hover:bg-destructive/5"
    data-comment-id={commentId}
    onClick={handleDeleteClick}
    onPointerDown={stopPropagation}
  >
    <Trash2 className="h-3 w-3" />
  </Button>
)
const CommentMeta = ({ comment, handleDeleteClick, stopPropagation }) => (
  <div className="flex items-center gap-2">
    <CommentTimestamp timestamp={comment.timestamp} />
    <CommentDeleteButton
      commentId={comment.id}
      handleDeleteClick={handleDeleteClick}
      stopPropagation={stopPropagation}
    />
  </div>
)
const CommentItem = ({ comment, handleDeleteClick, stopPropagation }) => (
  <motion.div
    key={comment.id}
    initial={{
      opacity: 0,
      y: 10,
    }}
    animate={{
      opacity: 1,
      y: 0,
    }}
    exit={{
      opacity: 0,
      y: -10,
    }}
    className="group relative flex gap-3"
  >
    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/10 flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-105">
      <span className="text-xs font-bold text-primary">
        {comment.userName
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()}
      </span>
    </div>
    <div className="flex-1 flex flex-col gap-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-foreground/80">{comment.userName}</span>
        <CommentMeta
          comment={comment}
          handleDeleteClick={handleDeleteClick}
          stopPropagation={stopPropagation}
        />
      </div>
      <div className="text-sm bg-muted/20 rounded-xl p-3 rounded-tl-none border border-border/40 text-foreground/90 shadow-sm group-hover:bg-muted/30 transition-colors break-words">
        {comment.text}
      </div>
    </div>
  </motion.div>
)
const EmptyComments = () => (
  <div className="flex flex-col items-center justify-center py-10 text-center bg-muted/5 rounded-xl border border-dashed border-border/30">
    <div className="h-12 w-12 rounded-full bg-muted/20 flex items-center justify-center mb-3">
      <MessageSquare className="h-6 w-6 text-muted-foreground/30" />
    </div>
    <p className="text-xs text-muted-foreground font-bold">No comments yet</p>
    <p className="text-xs text-muted-foreground/50 mt-1">Be the first to share your thoughts!</p>
  </div>
)
export function TaskComments({ todoId, comments, onAddComment, onDeleteComment }) {
  const [newComment, setNewComment] = useState('')

  const { completion: commentCompletion, acceptCompletion: acceptCommentCompletion } =
    useTaskAutocomplete(newComment, true, { type: 'comment', parentId: todoId })

  const handleSubmit = useCallback(
    (e) => {
      if (e) e.preventDefault()
      if (newComment.trim()) {
        onAddComment(newComment.trim())
        setNewComment('')
      }
    },
    [newComment, onAddComment],
  )
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleDeleteClick = useCallback(
    (e) => {
      e.stopPropagation()
      const commentId = e.currentTarget.dataset.commentId
      if (commentId) {
        onDeleteComment(commentId)
      }
    },
    [onDeleteComment],
  )
  const handleNewCommentChange = useCallback((e) => {
    setNewComment(e.target.value)
  }, [])
  const handleNewCommentKeyDown = useCallback(
    (e) => {
      if (e.key === 'Tab' && commentCompletion) {
        e.preventDefault()
        setNewComment((current) => acceptCommentCompletion(current))
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        handleSubmit(e)
      }
    },
    [handleSubmit, commentCompletion, acceptCommentCompletion],
  )
  return (
    <div className="flex flex-col gap-4 mt-6 pt-6 border-t border-border/20">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground/70 flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" />
          Comments
          <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full font-bold">
            {comments.length}
          </span>
        </h4>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        <AnimatePresence initial={false}>
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              handleDeleteClick={handleDeleteClick}
              stopPropagation={stopPropagation}
            />
          ))}
        </AnimatePresence>

        {comments.length === 0 && <EmptyComments />}
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative flex items-center gap-2 mt-2"
        onPointerDown={stopPropagation}
      >
        <div className="relative flex-1">
          {commentCompletion && (
            <div className="pointer-events-none absolute inset-0 z-0 px-3 py-[11px] text-sm leading-none whitespace-pre overflow-hidden">
              <span className="opacity-0">{newComment}</span>
              <span className="text-muted-foreground/50">{commentCompletion}</span>
            </div>
          )}
          <Input
            placeholder="Add a comment..."
            value={newComment}
            onChange={handleNewCommentChange}
            onKeyDown={handleNewCommentKeyDown}
            className="relative z-10 w-full h-11 bg-muted/30 border-2 border-transparent focus-visible:border-primary/20 focus-visible:bg-background focus-visible:ring-0 rounded-md pr-12 text-sm transition-all shadow-none"
            onPointerDown={stopPropagation}
          />
        </div>
        <Button
          type="submit"
          size="icon"
          className={cn(
            'absolute right-1.5 h-8 w-8 rounded-sm transition-all duration-300 z-20',
            newComment.trim()
              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-100'
              : 'bg-muted/50 text-muted-foreground opacity-50 scale-90',
          )}
          disabled={!newComment.trim()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  )
}
